import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { App, Alert, Button, Card, Dropdown, Form, Input, Modal, Select, Segmented, Space } from 'antd';
import { Upload } from 'antd';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { DeleteOutlined } from '@ant-design/icons';

const { TextArea } = Input;

const slugify = (s = '') => s
    .toString()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '')
    .slice(0, 160);

const ReleaseEditor = () => {
    const { message, modal } = App.useApp();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [metaForm] = Form.useForm();
    const { slug: routeSlug } = useParams();

    const [created, setCreated] = useState({ id: null, slug: '' });
    const [saving, setSaving] = useState(false);

    const [availableLangs, setAvailableLangs] = useState(['de','en','fr','it']);
    const [selectedLangs, setSelectedLangs] = useState(['de','en','fr','it']);
    const [activeLang, setActiveLang] = useState(localStorage.getItem('locale') || 'en');

    const [i18nForms, setI18nForms] = useState({});
    const [bodyMode, setBodyMode] = useState('rich'); // 'rich' | 'html'
    const [pairLoading, setPairLoading] = useState(false);
    const [missingLoading, setMissingLoading] = useState(false);
    const [uploadingThumbnail, setUploadingThumbnail] = useState(false);

    useEffect(() => {
        const init = async () => {
            try {
                const res = await axios.get('/v1/settings/languages');
                const langs = res.data?.data || ['de','en','fr','it'];
                setAvailableLangs(langs);
                setSelectedLangs(langs);
                const initialActive = langs.includes('en') ? 'en' : langs[0];
                const stored = localStorage.getItem('locale') || initialActive;
                setActiveLang(stored);
                const blank = {};
                langs.forEach(k => { blank[k] = { title: '', summary: '', body: '', hero_image_url: '' }; });
                setI18nForms(blank);
                metaForm.setFieldsValue({ status: 'draft' });

                // If editing, load the release by slug and hydrate
                if (routeSlug) {
                    const v = await axios.get(`/v1/releases/${encodeURIComponent(routeSlug)}?locale=${encodeURIComponent(stored)}`);
                    const data = v.data?.data;
                    if (data) {
                        setCreated({ id: data.id, slug: data.slug });
                        metaForm.setFieldsValue({ status: data.status || 'draft', version: data.version || '' });
                        const forms = { ...blank };
                        // Base locale (response fields)
                        if (data.language || data.locale) {
                            const baseLoc = data.language || data.locale;
                            forms[baseLoc] = {
                                title: data.title || '',
                                summary: data.summary || '',
                                body: data.body || '',
                                hero_image_url: data.hero_image_url || '',
                            };
                        }
                        // Translations array
                        (data.translations || []).forEach(t => {
                            if (!t?.locale) return;
                            forms[t.locale] = {
                                title: t.title || forms[t.locale]?.title || '',
                                summary: t.summary || forms[t.locale]?.summary || '',
                                body: t.body || forms[t.locale]?.body || '',
                                hero_image_url: t.hero_image_url || forms[t.locale]?.hero_image_url || '',
                            };
                        });
                        setI18nForms(forms);
                    }
                }
            } catch {}
        };
        init();
    }, [form, metaForm, routeSlug]);

    // Auto-generate slug from current activeLang title
    const currentTitle = i18nForms[activeLang]?.title || '';
    const computedSlug = useMemo(() => slugify(currentTitle), [currentTitle]);

    const submit = async () => {
        setSaving(true);
        try {
            const values = metaForm.getFieldsValue();
            const payload = {
                slug: routeSlug ? created.slug : computedSlug,
                status: values.status,
                version: values.version || null,
                locale: activeLang,
                title: i18nForms[activeLang]?.title || '',
                summary: i18nForms[activeLang]?.summary || '',
                body: i18nForms[activeLang]?.body || '',
            };
            if (created.id) {
                await axios.put(`/v1/releases/${created.id}`, payload);
                message.success('Updated');
            } else {
                const res = await axios.post('/v1/releases', payload);
                const id = res.data?.data?.id;
                const slug = res.data?.data?.slug || computedSlug;
                setCreated({ id, slug });
                message.success('Saved');
                navigate(`/admin/releases/edit/${slug}`);
            }
        } catch (e) {
            console.error('Save error:', e.response?.data || e);
            message.error(`Save failed: ${e.response?.data?.message || e.message}`);
        } finally {
            setSaving(false);
        }
    };

    const saveTranslations = async () => {
        if (!created.id) { message.warning('Save basics first.'); return; }
        const translations = selectedLangs.map((locale) => ({
            locale,
            title: i18nForms[locale]?.title || '',
            summary: i18nForms[locale]?.summary || '',
            body: i18nForms[locale]?.body || '',
        }));
        try {
            await axios.post(`/v1/releases/${created.id}/translations`, { translations });
            message.success('Translations saved');
        } catch {
            message.error('Save translations failed');
        }
    };

    const handleDelete = async () => {
        if (!created.id) { message.warning('Save basics first.'); return; }

        modal.confirm({
            title: 'Delete Release',
            content: (
                <div>
                    <p>Are you sure you want to delete this release?</p>
                    <p><strong>{i18nForms[activeLang]?.title || created.slug || 'Untitled Release'}</strong></p>
                    <p style={{ color: '#ff4d4f', fontSize: '14px' }}>This action cannot be undone.</p>
                </div>
            ),
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await axios.delete(`/v1/releases/${created.id}`);
                    message.success('Release deleted successfully');
                    navigate('/admin/releases'); // Redirect to admin releases list
                } catch (error) {
                    console.error('Delete error:', error);
                    message.error(`Failed to delete release: ${error.response?.data?.message || error.message}`);
                }
            },
        });
    };

    const aiTranslate = async (fromLocale, toLocale) => {
        if (!created.id) { message.warning('Save basics first.'); return; }
        if (fromLocale === toLocale) { message.info('Select different languages'); return; }
        setPairLoading(true);
        try {
            const res = await axios.post(`/v1/releases/${created.id}/translate`, { source_locale: fromLocale, target_locale: toLocale });
            const data = res.data?.data;
            if (data?.locale === toLocale) {
                // Overwrite destination fields unconditionally as requested
                setI18nForms(prev => ({
                    ...prev,
                    [toLocale]: {
                        title: data.title || '',
                        summary: data.summary || '',
                        body: data.body || '',
                        hero_image_url: prev[toLocale]?.hero_image_url || ''
                    }
                }));
                message.success(`Translated ${fromLocale.toUpperCase()} → ${toLocale.toUpperCase()}`);
            }
        } catch {
            message.error('AI translate failed');
        } finally {
            setPairLoading(false);
        }
    };

    const aiTranslateMissing = async (fromLocale) => {
        if (!created.id) { message.warning('Save basics first.'); return; }
        const src = i18nForms[fromLocale] || { title: '', summary: '', body: '' };
        if (!src.title && !src.summary && !src.body) {
            Modal.warning({ title: 'Source language is empty', content: `Please enter title/summary/body for ${fromLocale.toUpperCase()} first.` });
            return;
        }
        setMissingLoading(true);
        const updates = { ...i18nForms };
        for (const locale of selectedLangs) {
            if (locale === fromLocale) continue;
            try {
                const res = await axios.post(`/v1/releases/${created.id}/translate`, { source_locale: fromLocale, target_locale: locale });
                const data = res.data?.data;
                if (data?.locale === locale) {
                    // Overwrite all fields for each target
                    updates[locale] = {
                        title: data.title || '',
                        summary: data.summary || '',
                        body: data.body || '',
                        hero_image_url: updates[locale]?.hero_image_url || ''
                    };
                }
            } catch {}
        }
        setI18nForms(updates);
        message.success('Translated missing fields');
        setMissingLoading(false);
    };

    const moreLangOptions = useMemo(() => {
        const remaining = availableLangs.filter(k => !selectedLangs.includes(k));
        return remaining.map(k => ({ key: k, label: k.toUpperCase(), onClick: () => setSelectedLangs([...selectedLangs, k]) }));
    }, [availableLangs, selectedLangs]);

    return (
        <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
            {created.slug && (
                <Alert type="success" showIcon message={<span>Created: <a href={`/releases/${created.slug}`} target="_blank" rel="noopener noreferrer">/releases/{created.slug}</a></span>} style={{ marginBottom: 12 }} />
            )}
            <div className="release-edit-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
                <style>{`@media (min-width: 992px){ .release-edit-grid{ grid-template-columns: 1.1fr 0.9fr !important; } }`}</style>

                <Card title="Release Details" variant="outlined">
                    <Form layout="vertical" onFinish={submit}>
                        <Form.Item label={<div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}><span>{`Title (${activeLang.toUpperCase()})`}</span><Button size="small" shape="round" onClick={() => aiTranslatePair('en')} disabled={!created.id || activeLang==='en'}>Translate</Button></div>} required>
                            <Input
                                value={i18nForms[activeLang]?.title || ''}
                                onChange={(e) => setI18nForms({ ...i18nForms, [activeLang]: { ...i18nForms[activeLang], title: e.target.value } })}
                                maxLength={200}
                            />
                        </Form.Item>
                        <Form.Item label={<div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}><span>{`Summary (${activeLang.toUpperCase()})`}</span><Button size="small" shape="round" onClick={() => aiTranslatePair('en')} disabled={!created.id || activeLang==='en'}>Translate</Button></div>}>
                            {bodyMode === 'rich' ? (
                                <ReactQuill theme="snow" value={i18nForms[activeLang]?.summary || ''} onChange={(val) => setI18nForms({ ...i18nForms, [activeLang]: { ...i18nForms[activeLang], summary: val } })} />
                            ) : (
                                <TextArea rows={4} value={i18nForms[activeLang]?.summary || ''} onChange={(e) => setI18nForms({ ...i18nForms, [activeLang]: { ...i18nForms[activeLang], summary: e.target.value } })} />
                            )}
                        </Form.Item>
                        <Form.Item label={<div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}><span>{`Body (${activeLang.toUpperCase()})`}</span><Button size="small" shape="round" onClick={() => aiTranslatePair('en')} disabled={!created.id || activeLang==='en'}>Translate</Button></div>}>
                            <div style={{ marginBottom: 8 }}>
                                <Segmented size="small" value={bodyMode} onChange={(val) => setBodyMode(val)} options={[{label:'Rich Text',value:'rich'},{label:'Only HTML Content',value:'html'}]} />
                                <div style={{ marginTop: 8 }}>
                                    {bodyMode === 'rich' ? (
                                        <ReactQuill theme="snow" value={i18nForms[activeLang]?.body || ''} onChange={(val) => setI18nForms({ ...i18nForms, [activeLang]: { ...i18nForms[activeLang], body: val } })} />
                                    ) : (
                                        <TextArea rows={12} placeholder="Paste HTML..." value={i18nForms[activeLang]?.body || ''} onChange={(e) => setI18nForms({ ...i18nForms, [activeLang]: { ...i18nForms[activeLang], body: e.target.value } })} />
                                    )}
                                </div>
                            </div>
                        </Form.Item>

                        {/* Hidden computed slug, display read-only */}
                        <Form.Item label="Slug (auto)" tooltip="Generated from title">
                            <Input value={computedSlug} readOnly />
                        </Form.Item>

                        {/* Thumbnail (hero) for active language */}
                        {created.id && (
                            <div style={{ marginBottom: 16 }}>
                                <label style={{ display: 'block', marginBottom: 6 }}>Thumbnail (active: {activeLang.toUpperCase()})</label>
                                <Upload.Dragger
                                    multiple={false}
                                    showUploadList={false}
                                    accept="image/*"
                                    disabled={uploadingThumbnail}
                                    customRequest={async ({ file, onSuccess, onError }) => {
                                        if (!created.id) {
                                            message.warning('Save first');
                                            onError && onError('No release ID');
                                            return;
                                        }
                                        if (uploadingThumbnail) {
                                            message.warning('Upload already in progress');
                                            onError && onError('Upload in progress');
                                            return;
                                        }

                                        setUploadingThumbnail(true);
                                        const fd = new FormData();
                                        fd.append('image', file);
                                        fd.append('locale', activeLang);
                                        fd.append('as_hero', '1');

                                        try {
                                            const res = await axios.post(`/v1/releases/${created.id}/media`, fd, {
                                                headers: { 'Content-Type': 'multipart/form-data' }
                                            });
                                            const url = res.data?.data?.url;
                                            if (url) {
                                                setI18nForms(prev => ({
                                                    ...prev,
                                                    [activeLang]: {
                                                        ...prev[activeLang],
                                                        hero_image_url: url
                                                    }
                                                }));
                                                message.success('Thumbnail uploaded');
                                                onSuccess && onSuccess(res.data);
                                            } else {
                                                throw new Error('No URL returned');
                                            }
                                        } catch (e) {
                                            console.error('Upload error:', e);
                                            message.error(`Thumbnail upload failed: ${e.response?.data?.message || e.message}`);
                                            onError && onError(e);
                                        } finally {
                                            setUploadingThumbnail(false);
                                        }
                                    }}
                                    style={{ minHeight: 140, height: 140, position: 'relative' }}
                                >
                                    {uploadingThumbnail ? (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                            <p>Uploading...</p>
                                        </div>
                                    ) : i18nForms[activeLang]?.hero_image_url ? (
                                        <img
                                            src={i18nForms[activeLang]?.hero_image_url}
                                            alt="Thumbnail"
                                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                                            onError={(e) => {
                                                console.error('Image load error:', e.target.src);
                                                // Remove the failed image URL to prevent infinite retries
                                                setI18nForms(prev => ({
                                                    ...prev,
                                                    [activeLang]: {
                                                        ...prev[activeLang],
                                                        hero_image_url: null
                                                    }
                                                }));
                                            }}
                                        />
                                    ) : (
                                        <p>Drop a JPG/PNG/WEBP or click to select</p>
                                    )}
                                </Upload.Dragger>
                            </div>
                        )}

                        <Form.Item>
                            <Space>
                                <Button type="primary" htmlType="submit" loading={saving}>Save</Button>
                                {created.id && (
                                    <Button
                                        type="default"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={handleDelete}
                                        title="Delete release"
                                    >
                                        Delete
                                    </Button>
                                )}
                            </Space>
                        </Form.Item>
                    </Form>
                </Card>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <Card title="Meta" variant="outlined">
                        <Form layout="vertical" form={metaForm}>
                            <Form.Item name="version" label="Version">
                                <Input placeholder="e.g. 1.2.3" />
                            </Form.Item>
                            <Form.Item name="status" label="Status" initialValue="draft">
                                <Select options={[{ value: 'draft', label: 'Draft' }, { value: 'review', label: 'Review' }, { value: 'scheduled', label: 'Scheduled' }, { value: 'published', label: 'Published' }]} />
                            </Form.Item>
                        </Form>
                    </Card>

                    <Card title="Translations" variant="outlined">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                            <Segmented size="middle" options={selectedLangs.map(k => ({ label: k.toUpperCase(), value: k }))} value={activeLang} onChange={(val) => setActiveLang(val)} />
                            <Dropdown menu={{ items: moreLangOptions }}>
                                <Button>More</Button>
                            </Dropdown>
                        </div>
                        <div style={{ color: '#888', marginBottom: 8 }}>
                            Switch language above to edit its content in the left panel. Use the AI buttons to auto-fill missing translations.
                        </div>
                        <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
                            <Dropdown
                                menu={{
                                    items: selectedLangs.map(l => ({
                                        key: l,
                                        label: `AI translate missing from ${l.toUpperCase()} → others`,
                                        onClick: () => aiTranslateMissing(l)
                                    }))
                                }}
                            >
                                <Button loading={missingLoading}>AI Translate Missing</Button>
                            </Dropdown>
                            <Dropdown
                                menu={{
                                    items: availableLangs.filter(l => l !== activeLang).map(l => ({
                                        key: l,
                                        label: `AI translate from ${l.toUpperCase()} → ${activeLang.toUpperCase()}`,
                                        onClick: () => aiTranslate(l, activeLang)
                                    }))
                                }}
                            >
                                <Button loading={pairLoading}>AI Translate</Button>
                            </Dropdown>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default ReleaseEditor;
